import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Search, 
  Mail, 
  MoreHorizontal, 
  Trash2, 
  Edit,
  Loader2,
  FolderTree,
  FileText,
  Package,
  ChevronRight,
  ChevronDown,
  Paperclip,
  Calendar,
  User,
  Link,
  Tag,
  X,
  Save,
  ExternalLink,
  Folder,
  FolderPlus
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Correspondence = () => {
  const { token, hasPermission } = useAuth();
  const [correspondence, setCorrespondence] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  
  // Detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCorrespondence, setSelectedCorrespondence] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit form data
  const [editFormData, setEditFormData] = useState({
    folder_id: '',
    document_ids: [],
    asset_ids: [],
    project_ids: [],
    tags: []
  });
  
  // Available entities for linking
  const [documents, setDocuments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Folder management modal (admin)
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderFormData, setFolderFormData] = useState({ name: '', parent_id: '', description: '' });
  const [editingFolder, setEditingFolder] = useState(null);

  const fetchCorrespondence = useCallback(async () => {
    try {
      let url = `${API_URL}/api/correspondence`;
      const params = new URLSearchParams();
      if (selectedFolderId) params.append('folder_id', selectedFolderId);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrespondence(response.data.correspondence);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load correspondence');
      }
    } finally {
      setLoading(false);
    }
  }, [token, selectedFolderId, searchTerm]);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/correspondence-folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data.folders);
    } catch (error) {
      console.error('Failed to load folders');
    }
  }, [token]);

  const fetchEntities = useCallback(async () => {
    try {
      const [docsRes, assetsRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDocuments(docsRes.data.documents || []);
      setAssets(assetsRes.data.assets || []);
      setProjects(projectsRes.data.projects || []);
    } catch (error) {
      console.error('Failed to load entities for linking');
    }
  }, [token]);

  useEffect(() => {
    fetchFolders();
    fetchEntities();
  }, [fetchFolders, fetchEntities]);

  useEffect(() => {
    fetchCorrespondence();
  }, [fetchCorrespondence]);

  const fetchCorrespondenceDetail = async (corrId) => {
    try {
      const response = await axios.get(`${API_URL}/api/correspondence/${corrId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCorrespondence(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load correspondence details');
    }
  };

  const startEditing = () => {
    if (!selectedCorrespondence) return;
    setEditFormData({
      folder_id: selectedCorrespondence.folder_id || '',
      document_ids: selectedCorrespondence.document_ids || [],
      asset_ids: selectedCorrespondence.asset_ids || [],
      project_ids: selectedCorrespondence.project_ids || [],
      tags: selectedCorrespondence.tags || []
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!selectedCorrespondence) return;
    setSaving(true);
    
    try {
      await axios.put(`${API_URL}/api/correspondence/${selectedCorrespondence.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Correspondence updated successfully');
      
      // Refresh detail
      const response = await axios.get(`${API_URL}/api/correspondence/${selectedCorrespondence.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCorrespondence(response.data);
      setIsEditing(false);
      fetchCorrespondence();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update correspondence');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (corrId) => {
    if (!window.confirm('Are you sure you want to delete this correspondence?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/correspondence/${corrId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Correspondence deleted');
      setShowDetailModal(false);
      fetchCorrespondence();
    } catch (error) {
      toast.error('Failed to delete correspondence');
    }
  };

  // Folder management functions
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/correspondence-folders`, {
        name: folderFormData.name,
        parent_id: folderFormData.parent_id || null,
        description: folderFormData.description || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder created');
      setShowFolderModal(false);
      setFolderFormData({ name: '', parent_id: '', description: '' });
      fetchFolders();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/correspondence-folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder deleted');
      if (selectedFolderId === folderId) setSelectedFolderId(null);
      fetchFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  // Toggle entity selection in edit mode
  const toggleEntitySelection = (type, id) => {
    setEditFormData(prev => ({
      ...prev,
      [type]: prev[type].includes(id)
        ? prev[type].filter(i => i !== id)
        : [...prev[type], id]
    }));
  };

  // Tag handling
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim();
      if (!editFormData.tags.includes(newTag)) {
        setEditFormData(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      }
      e.target.value = '';
    }
  };

  const removeTag = (tag) => {
    setEditFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  // Build folder tree
  const rootFolders = folders.filter(f => !f.parent_id);
  const getChildFolders = (parentId) => folders.filter(f => f.parent_id === parentId);

  const toggleFolderExpanded = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  // Render folder tree item
  const renderFolderItem = (folder, level = 0) => {
    const children = getChildFolders(folder.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolderId === folder.id;

    return (
      <div key={folder.id}>
        <div 
          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
            isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => setSelectedFolderId(isSelected ? null : folder.id)}
        >
          {hasChildren ? (
            <button 
              onClick={(e) => { e.stopPropagation(); toggleFolderExpanded(folder.id); }}
              className="p-0.5 hover:bg-muted rounded"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="w-5" />
          )}
          <Folder className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm flex-1 truncate">{folder.name}</span>
          {hasPermission('manage_correspondence') && (
            <button 
              onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded"
            >
              <Trash2 className="w-3 h-3 text-destructive" />
            </button>
          )}
        </div>
        {isExpanded && children.map(child => renderFolderItem(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Correspondence</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage imported email correspondence
          </p>
        </div>
        {hasPermission('manage_correspondence') && (
          <Button onClick={() => setShowFolderModal(true)} variant="outline">
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </Button>
        )}
      </div>

      <div className="p-8">
        <div className="flex gap-6">
          {/* Folder Tree Sidebar */}
          <div className="w-64 shrink-0">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <FolderTree className="w-4 h-4" />
                  Folders
                </h3>
                <div className="space-y-1">
                  <div 
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      !selectedFolderId ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedFolderId(null)}
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">All Correspondence</span>
                  </div>
                  {rootFolders.map(folder => renderFolderItem(folder))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search correspondence..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Correspondence List */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : correspondence.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No correspondence found</p>
                    {hasPermission('manage_correspondence') && (
                      <p className="text-sm mt-2">Import emails from the Mail Import page</p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-border" data-testid="correspondence-list">
                    {correspondence.map((corr) => (
                      <div 
                        key={corr.id}
                        data-testid={`correspondence-item-${corr.id}`}
                        className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => fetchCorrespondenceDetail(corr.id)}
                      >
                        <div className="flex items-start gap-4">
                          <div className="p-2 rounded-lg bg-muted">
                            <Mail className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium truncate">{corr.subject}</h4>
                              {corr.attachments?.length > 0 && (
                                <Paperclip className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              From: {corr.from_address}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {corr.date_sent ? format(new Date(corr.date_sent), 'MMM d, yyyy HH:mm') : 'Unknown'}
                              </span>
                              {corr.folder_name && (
                                <span className="flex items-center gap-1">
                                  <Folder className="w-3 h-3" />
                                  {corr.folder_name}
                                </span>
                              )}
                            </div>
                            {/* Linked entities badges */}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {corr.linked_documents?.slice(0, 2).map(doc => (
                                <Badge key={doc.id} variant="outline" className="text-xs">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {doc.name}
                                </Badge>
                              ))}
                              {corr.linked_assets?.slice(0, 2).map(asset => (
                                <Badge key={asset.id} variant="secondary" className="text-xs">
                                  <Package className="w-3 h-3 mr-1" />
                                  {asset.name}
                                </Badge>
                              ))}
                              {(corr.linked_documents?.length > 2 || corr.linked_assets?.length > 2) && (
                                <Badge variant="outline" className="text-xs">+more</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Correspondence Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        if (!open) { setIsEditing(false); }
        setShowDetailModal(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedCorrespondence && (
            <>
              <DialogHeader className="flex flex-row items-start justify-between">
                <div className="flex-1">
                  <DialogTitle className="flex items-center gap-3">
                    <Mail className="w-5 h-5" />
                    {selectedCorrespondence.subject}
                  </DialogTitle>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {!isEditing ? (
                    hasPermission('manage_correspondence') && (
                      <>
                        <Button variant="outline" size="sm" onClick={startEditing}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Links
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(selectedCorrespondence.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={cancelEditing}>Cancel</Button>
                      <Button size="sm" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Email metadata */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase">From</Label>
                    <p className="font-medium">{selectedCorrespondence.from_address}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase">Date</Label>
                    <p className="font-medium">
                      {selectedCorrespondence.date_sent 
                        ? format(new Date(selectedCorrespondence.date_sent), 'MMM d, yyyy HH:mm')
                        : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase">To</Label>
                    <p className="font-medium">{selectedCorrespondence.to_addresses?.join(', ') || '-'}</p>
                  </div>
                  {selectedCorrespondence.cc_addresses?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase">CC</Label>
                      <p className="font-medium">{selectedCorrespondence.cc_addresses.join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Email body */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">Message</Label>
                  <div className="p-4 bg-background border rounded-lg max-h-60 overflow-y-auto">
                    {selectedCorrespondence.body_html ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedCorrespondence.body_html }} />
                    ) : (
                      <pre className="whitespace-pre-wrap text-sm font-sans">
                        {selectedCorrespondence.body_text || 'No content'}
                      </pre>
                    )}
                  </div>
                </div>

                {/* Attachments */}
                {selectedCorrespondence.attachments?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                      <Paperclip className="w-3 h-3 inline mr-1" />
                      Attachments
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedCorrespondence.attachments.map(att => (
                        <a
                          key={att.id}
                          href={`${API_URL}/api/correspondence/${selectedCorrespondence.id}/attachments/${att.id}/download`}
                          className="flex items-center gap-2 p-2 bg-muted rounded hover:bg-muted/80 transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{att.filename}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(att.size / 1024).toFixed(1)} KB)
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Folder (editable by admin) */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                    <Folder className="w-3 h-3 inline mr-1" />
                    Folder
                  </Label>
                  {isEditing ? (
                    <Select
                      value={editFormData.folder_id || "none"}
                      onValueChange={(value) => setEditFormData({ ...editFormData, folder_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="edit-folder-select">
                        <SelectValue placeholder="Select folder" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Folder</SelectItem>
                        {(() => {
                          // Build hierarchical folder options with path
                          const getFolderPath = (folder) => {
                            const parts = [folder.name];
                            let current = folder;
                            while (current.parent_id) {
                              const parent = folders.find(f => f.id === current.parent_id);
                              if (parent) {
                                parts.unshift(parent.name);
                                current = parent;
                              } else break;
                            }
                            return parts.join(' / ');
                          };
                          
                          // Sort folders to show hierarchy
                          const sortedFolders = [...folders].sort((a, b) => {
                            const pathA = getFolderPath(a);
                            const pathB = getFolderPath(b);
                            return pathA.localeCompare(pathB);
                          });
                          
                          return sortedFolders.map(folder => (
                            <SelectItem key={folder.id} value={folder.id}>
                              {getFolderPath(folder)}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  ) : selectedCorrespondence.folder ? (
                    <Badge variant="secondary">{selectedCorrespondence.folder.name}</Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">No folder assigned</p>
                  )}
                </div>

                {/* Linked Documents */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                    <FileText className="w-3 h-3 inline mr-1" />
                    Linked Documents
                  </Label>
                  {isEditing ? (
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No documents available</p>
                      ) : (
                        <div className="space-y-2">
                          {documents.map(doc => (
                            <div 
                              key={doc.id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                editFormData.document_ids.includes(doc.id)
                                  ? 'bg-primary/10 border border-primary/30'
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleEntitySelection('document_ids', doc.id)}
                            >
                              <Checkbox checked={editFormData.document_ids.includes(doc.id)} onChange={() => {}} />
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{doc.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : selectedCorrespondence.linked_documents?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedCorrespondence.linked_documents.map(doc => (
                        <Badge key={doc.id} variant="outline">
                          <FileText className="w-3 h-3 mr-1" />
                          {doc.document_number && <span className="font-mono mr-1">{doc.document_number}</span>}
                          {doc.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No documents linked</p>
                  )}
                </div>

                {/* Linked Assets */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                    <Package className="w-3 h-3 inline mr-1" />
                    Linked Assets
                  </Label>
                  {isEditing ? (
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {assets.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No assets available</p>
                      ) : (
                        <div className="space-y-2">
                          {assets.map(asset => (
                            <div 
                              key={asset.id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                editFormData.asset_ids.includes(asset.id)
                                  ? 'bg-primary/10 border border-primary/30'
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleEntitySelection('asset_ids', asset.id)}
                            >
                              <Checkbox checked={editFormData.asset_ids.includes(asset.id)} onChange={() => {}} />
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{asset.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : selectedCorrespondence.linked_assets?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedCorrespondence.linked_assets.map(asset => (
                        <Badge key={asset.id} variant="secondary">
                          <Package className="w-3 h-3 mr-1" />
                          {asset.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No assets linked</p>
                  )}
                </div>

                {/* Linked Projects */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                    <FolderTree className="w-3 h-3 inline mr-1" />
                    Linked Projects
                  </Label>
                  {isEditing ? (
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {projects.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No projects available</p>
                      ) : (
                        <div className="space-y-2">
                          {projects.map(project => (
                            <div 
                              key={project.id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                editFormData.project_ids.includes(project.id)
                                  ? 'bg-primary/10 border border-primary/30'
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleEntitySelection('project_ids', project.id)}
                            >
                              <Checkbox checked={editFormData.project_ids.includes(project.id)} onChange={() => {}} />
                              <FolderTree className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{project.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : selectedCorrespondence.linked_projects?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedCorrespondence.linked_projects.map(project => (
                        <Badge key={project.id} variant="outline">
                          <FolderTree className="w-3 h-3 mr-1" />
                          {project.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No projects linked</p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                    <Tag className="w-3 h-3 inline mr-1" />
                    Tags
                  </Label>
                  {isEditing ? (
                    <div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {editFormData.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => removeTag(tag)} />
                          </Badge>
                        ))}
                      </div>
                      <Input
                        placeholder="Type and press Enter to add tags..."
                        onKeyDown={handleTagKeyDown}
                        className="h-8"
                      />
                    </div>
                  ) : selectedCorrespondence.tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedCorrespondence.tags.map((tag, i) => (
                        <Badge key={i} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tags</p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button onClick={() => setShowDetailModal(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Folder Modal */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFolder} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                value={folderFormData.name}
                onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent-folder">Parent Folder</Label>
              <Select
                value={folderFormData.parent_id || "none"}
                onValueChange={(value) => setFolderFormData({ ...folderFormData, parent_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None (root level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (root level)</SelectItem>
                  {folders.map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder-description">Description</Label>
              <Input
                id="folder-description"
                value={folderFormData.description}
                onChange={(e) => setFolderFormData({ ...folderFormData, description: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFolderModal(false)}>Cancel</Button>
              <Button type="submit">Create Folder</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Correspondence;
